export class RulePartClassifier {
	classifyPageRuleParts(page, documentNode) {
		const selectorsByRule = page?.failedSelectorsByRule || {};
		const headerSelector = page?.layoutSignals?.header?.selector || '';
		const footerSelector = page?.layoutSignals?.footer?.selector || '';
		const context = this.createDetectionContext(documentNode, headerSelector, footerSelector);
		const partsByRule = {};

		for (const [ruleKey, selectors] of Object.entries(selectorsByRule)) {
			const summary = {
				header: 0,
				footer: 0,
				page: 0,
				labels: [],
				selectorParts: {}
			};

			if (!Array.isArray(selectors) || selectors.length === 0 || !documentNode) {
				partsByRule[ruleKey] = summary;
				continue;
			}

			for (const selector of selectors) {
				if (typeof selector !== 'string' || selector.length === 0) {
					continue;
				}

				const selectorPartSet = new Set();

				let matchedNodes = [];

				try {
					matchedNodes = Array.from(documentNode.querySelectorAll(selector));
				} catch {
					matchedNodes = [];
				}

				if (matchedNodes.length === 0) {
					summary.page += 1;
					selectorPartSet.add('page');
					summary.selectorParts[selector] = Array.from(selectorPartSet);
					continue;
				}

				for (const node of matchedNodes) {
					const part = this.classifyNodePart(node, context);

					if (part === 'header') {
						summary.header += 1;
						selectorPartSet.add('header');
						continue;
					}

					if (part === 'footer') {
						summary.footer += 1;
						selectorPartSet.add('footer');
						continue;
					}

					summary.page += 1;
					selectorPartSet.add('page');
				}

				summary.selectorParts[selector] = Array.from(selectorPartSet);
			}

			if (summary.header > 0) {
				summary.labels.push('header');
			}

			if (summary.footer > 0) {
				summary.labels.push('footer');
			}

			if (summary.page > 0) {
				summary.labels.push('page');
			}

			partsByRule[ruleKey] = summary;
		}

		return partsByRule;
	}

	createDetectionContext(documentNode, headerSelector, footerSelector) {
		return {
			documentNode,
			headerContainers: this.collectContainers(documentNode, headerSelector, this.getHeaderFallbackSelectors()),
			footerContainers: this.collectContainers(documentNode, footerSelector, this.getFooterFallbackSelectors())
		};
	}

	getHeaderFallbackSelectors() {
		return [
			'header',
			'[role="banner"]',
			'#header',
			'#masthead',
			'.site-header',
			'.main-header',
			'.masthead',
			'.header'
		];
	}

	getFooterFallbackSelectors() {
		return [
			'footer',
			'[role="contentinfo"]',
			'#footer',
			'#colophon',
			'.site-footer',
			'.main-footer',
			'.colophon',
			'.footer'
		];
	}

	collectContainers(documentNode, primarySelector, fallbackSelectors) {
		if (!documentNode) {
			return [];
		}

		const nodeSet = new Set();
		const selectors = [];

		if (typeof primarySelector === 'string' && primarySelector.trim().length > 0) {
			selectors.push(primarySelector.trim());
		}

		for (const fallbackSelector of fallbackSelectors) {
			if (typeof fallbackSelector === 'string' && fallbackSelector.length > 0) {
				selectors.push(fallbackSelector);
			}
		}

		for (const selector of selectors) {
			let nodes = [];

			try {
				nodes = Array.from(documentNode.querySelectorAll(selector));
			} catch {
				nodes = [];
			}

			for (const node of nodes) {
				nodeSet.add(node);
			}
		}

		return Array.from(nodeSet);
	}

	classifyNodePart(node, context) {
		if (!node) {
			return 'page';
		}

		if (this.isWithinContainers(node, context?.footerContainers || [])) {
			return 'footer';
		}

		if (this.isWithinContainers(node, context?.headerContainers || [])) {
			return 'header';
		}

		const footerTokenMatch = this.hasAncestorToken(node, ['footer', 'colophon', 'site-footer', 'main-footer']);

		if (footerTokenMatch) {
			return 'footer';
		}

		const headerTokenMatch = this.hasAncestorToken(node, ['header', 'masthead', 'site-header', 'main-header', 'topbar']);

		if (headerTokenMatch) {
			return 'header';
		}

		return 'page';
	}

	isWithinContainers(node, containers) {
		if (!node || !Array.isArray(containers) || containers.length === 0) {
			return false;
		}

		for (const containerNode of containers) {
			if (containerNode?.contains(node)) {
				return true;
			}
		}

		return false;
	}

	hasAncestorToken(node, tokens) {
		if (!node || !Array.isArray(tokens) || tokens.length === 0) {
			return false;
		}

		let currentNode = node;
		let depth = 0;

		while (currentNode && depth < 6) {
			const idText = typeof currentNode.id === 'string' ? currentNode.id.toLowerCase() : '';
			const classText = typeof currentNode.className === 'string' ? currentNode.className.toLowerCase() : '';

			for (const token of tokens) {
				if (idText.includes(token) || classText.includes(token)) {
					return true;
				}
			}

			currentNode = currentNode.parentElement;
			depth += 1;
		}

		return false;
	}

	isWithinSelector(node, containerSelector) {
		if (!node || typeof containerSelector !== 'string' || containerSelector.length === 0) {
			return false;
		}

		let containerNodes = [];

		try {
			containerNodes = Array.from(node.ownerDocument.querySelectorAll(containerSelector));
		} catch {
			containerNodes = [];
		}

		if (containerNodes.length === 0) {
			return false;
		}

		for (const containerNode of containerNodes) {
			if (containerNode.contains(node)) {
				return true;
			}
		}

		return false;
	}
}
